// Toast.tsx
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import { FC, useEffect, useState } from "react";
import { camelCaseToTitleCase } from "util/helper/helper";
import HideIf from "util/HideIf";
import styles from "./toast.module.scss";

export type ToastType = "success" | "error" | "warning" | "success-secondary";

interface ToastProps {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  onClose?: (id: string) => void;
  iconClass?: string;
  showOnlyContent?: boolean;
}

const Toast: FC<ToastProps> = ({
  id, message, type, duration = 3000, onClose, iconClass, showOnlyContent,
}) => {
  const MainContent = () => (
    <>
      <i className={`fa ${iconClass}`} />
      <div className={styles.toast_message_container}>
        <HideIf show={!showOnlyContent}>
          <PAText fontSize={FONT_SIZE[2]} bold component="h4" className="m-b-0">{`${camelCaseToTitleCase(type)}!`}</PAText>
        </HideIf>
        <PAText component="p" className="m-t-5">{message}</PAText>
      </div>
    </>
  );

  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (duration > 0) {
      timer = setTimeout(() => {
        setExiting(true);
        setTimeout(() => (onClose ? onClose(id) : ""), 300); // Match exit animation duration
      }, duration);
    }
    return () => clearTimeout(timer);
  }, [duration, id, onClose]);

  let toastClass = "";

  const handleClose = () => {
    setExiting(true);
    setTimeout(() => onClose(id), 300); // Match exit animation duration
  };

  if (showOnlyContent) {
    toastClass = `${styles.toast} ${styles[`toast--${type}`]} ${styles[`toast--${type}-background`]}`;
    return (
      <div className={toastClass}>
        <MainContent />
      </div>
    );
  }
  toastClass = `${styles.toast} ${styles.toast_slide} ${styles[`toast--${type}`]} ${exiting ? styles.toastExiting : ""}`;

  return (
    <div className={toastClass} role="alert" aria-live="assertive" aria-atomic="true">
      <MainContent />
      <button
        type="button"
        className={styles.closeButton}
        onClick={handleClose}
        aria-label="Close notification"
      >
        &times;
      </button>
    </div>
  );
};

export default Toast;
